import axios from 'axios';
import { toast } from 'sonner';
import { useCompanyStore } from '../store/companyStore';
import { useAuthStore } from '../store/authStore';

const BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
export const API = `${BASE}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  // Super admins can act on behalf of a company — inject the active company_id.
  // Regular users get company_id from the JWT on the backend.
  const { user } = useAuthStore.getState();
  if (user?.role === 'super_admin') {
    const { activeCompany } = useCompanyStore.getState();
    const companyId = activeCompany?.id;
    if (companyId) {
      const method = (config.method || 'get').toLowerCase();
      if (method === 'get' || method === 'delete') {
        if (!config.params?.company_id) {
          config.params = { ...config.params, company_id: companyId };
        }
      } else if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
        if (!config.data.company_id) config.data = { ...config.data, company_id: companyId };
      }
    }
  }
  return config;
});

// Extract the most specific human-readable message from an axios error.
// Backend validation failures return `{ error: 'Validation failed', details: [{ field, msg }] }`;
// without this, callers only see the generic top-level `error` and lose the field-level reason.
export const apiError = (err, fallback = 'Something went wrong') => {
  const data = err?.response?.data;
  if (data?.details?.length) {
    return data.details
      .map((d) => (d.field ? `${d.field}: ${d.msg}` : d.msg))
      .join('; ');
  }
  return data?.error || err?.message || fallback;
};

let refreshing = null;
const tryRefresh = () => {
  if (!refreshing) {
    refreshing = api.post('/auth/refresh').finally(() => { refreshing = null; });
  }
  return refreshing;
};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || '';

    // Don't try to refresh the refresh/login/logout calls themselves.
    const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/logout');

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        await tryRefresh();
        return api(original);
      } catch (_) {
        useAuthStore.getState().clear();
        useCompanyStore.getState().clearCompany();
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
        return Promise.reject(err);
      }
    }

    if (status === 401 && isAuthEndpoint) {
      useAuthStore.getState().clear();
    }

    // Plan gate (402) — surface as a friendly toast with a one-click jump to
    // /billing. Sidebar already locks the nav for known feature flags, but a
    // direct deep-link or in-page action can still hit a 402; this catches it.
    if (status === 402) {
      const body = err.response?.data || {};
      const code = body.code;
      if (code === 'FEATURE_NOT_INCLUDED' || code === 'PLAN_LIMIT_REACHED') {
        const msg = body.error || 'This feature is not included in your current plan.';
        toast.error(msg, {
          action: {
            label: 'Upgrade',
            onClick: () => { window.location.href = '/billing'; },
          },
        });
        // Mark so per-call .catch() blocks can opt-out of re-showing the error
        err._planBlockToasted = true;
      }
    }

    return Promise.reject(err);
  }
);

export default api;
